import React from "react";
import styled from "styled-components";
import { useRouteMatch } from "react-router-dom";
import Tabs from "common/Tabs/Tabs";
import useTabsWithUrl from "common/Tabs/useTabsWithUrl";
import { TabItem } from "common/Tabs/PageTabs";
import {
  CLOSED_TRADES_PATH,
  OPEN_TRADES_PATH,
  TRADES_PATH,
} from "common/constants";
import { TradesTab } from "@shared/features/trades/common/types";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types";

/** Open / Closed tabs rendered under the new-look page title. */
const NewLookTradesTabs = () => {
  const { t } = useTypedTranslation(LanguageNameSpaces.TRADES);
  const tabToDisplayName = {
    [TradesTab.OPEN]: t("tabs.open"),
    [TradesTab.CLOSED]: t("tabs.closed"),
  };

  // These tabs live in the page toolbar, which renders outside the route and
  // outlives the Trades page for one render when you navigate away. Only
  // complete the URL (/trades → /trades/open) while still on Trades, or the
  // tabs would send the new page back here.
  const isOnTrades = !!useRouteMatch(TRADES_PATH);
  const { tabDisplayName, onChange } = useTabsWithUrl<TradesTab>({
    tabToDisplayName,
    tabs: Object.values(TradesTab),
    path: TRADES_PATH,
    defaultTab: TradesTab.OPEN,
    keepQuery: false,
    skipUrlInitialization: !isOnTrades,
  });

  const items: TabItem[] = [
    {
      key: tabToDisplayName[TradesTab.OPEN],
      label: tabToDisplayName[TradesTab.OPEN],
      link: OPEN_TRADES_PATH,
    },
    {
      key: tabToDisplayName[TradesTab.CLOSED],
      label: tabToDisplayName[TradesTab.CLOSED],
      link: CLOSED_TRADES_PATH,
    },
  ];

  return (
    <StyledTabs activeKey={tabDisplayName} onChange={onChange} items={items} />
  );
};

export default React.memo(NewLookTradesTabs);

const StyledTabs = styled(Tabs)`
  .ant-tabs-nav {
    margin-bottom: 0;
  }
  /* The tabs sit in the toolbar row: no full-width hairline, just the ink bar. */
  .ant-tabs-nav::before {
    display: none;
  }
  .ant-tabs-tab {
    padding: 6px 0 8px;
    font-size: 14px;
  }
  .ant-tabs-tab + .ant-tabs-tab {
    margin-left: 24px;
  }
  .ant-tabs-tab-btn {
    color: ${(p) => p.theme.colors.secondaryText};
  }
`;
